import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { auditApi } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { formatDateTime } from '@/lib/utils';
import { Search, Download, Filter, User, Bot, Globe } from 'lucide-react';
import { usePermissions } from '@/hooks/use-permissions';
import { QueryState } from '@/components/query-state';

function AuditSkeleton() {
  return (
    <div className="space-y-3">
      {[1, 2, 3, 4, 5].map((i) => (
        <Card key={i} className="animate-pulse">
          <CardContent className="p-4">
            <div className="flex items-center gap-4">
              <div className="h-8 w-8 rounded-full bg-muted" />
              <div className="space-y-2 flex-1">
                <div className="h-4 w-48 bg-muted rounded" />
                <div className="h-3 w-32 bg-muted rounded" />
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

export default function AuditPage() {
  const [search, setSearch] = useState('');
  const [showEpoOnly, setShowEpoOnly] = useState(false);
  const permissions = usePermissions();

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['audit-events', search, showEpoOnly],
    queryFn: () =>
      showEpoOnly
        ? auditApi.getEpoApprovals({ limit: 50 })
        : auditApi.list({ action: search || undefined, limit: 50 }),
  });

  const events = data?.data || [];

  const handleExport = async (format: 'json' | 'csv') => {
    try {
      const content = await auditApi.export(format, {
        action: search || undefined,
      });

      const blob = new Blob([content], {
        type: format === 'json' ? 'application/json' : 'text/csv',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `audit-export-${Date.now()}.${format}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Export failed:', error);
    }
  };

  const getActorIcon = (type: string) => {
    switch (type) {
      case 'user':
        return <User className="h-4 w-4" />;
      case 'system':
        return <Bot className="h-4 w-4" />;
      case 'public':
        return <Globe className="h-4 w-4" />;
      default:
        return <User className="h-4 w-4" />;
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <span className="rf3-eyebrow">Administration</span>
          <h2 className="text-3xl font-bold tracking-tight text-primary">Audit logs</h2>
          <p className="text-muted-foreground">
            View security-sensitive actions across the suite
          </p>
        </div>
        {permissions.canExportAudit && (
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => handleExport('csv')}>
              <Download className="mr-2 h-4 w-4" />
              Export CSV
            </Button>
            <Button variant="outline" onClick={() => handleExport('json')}>
              <Download className="mr-2 h-4 w-4" />
              Export JSON
            </Button>
          </div>
        )}
      </div>

      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Filter by action..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
        <Button
          variant={showEpoOnly ? 'default' : 'outline'}
          onClick={() => setShowEpoOnly(!showEpoOnly)}
        >
          <Filter className="mr-2 h-4 w-4" />
          EPO Approvals
        </Button>
      </div>

      <QueryState
        isLoading={isLoading}
        isError={isError}
        error={error}
        isEmpty={events.length === 0}
        skeleton={<AuditSkeleton />}
        emptyMessage="No audit events found"
      >
        <div className="space-y-2">
          {events.map((event) => (
            <Card key={event.event_id} className="hover:bg-muted/50 transition-colors">
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-muted rounded-full">
                      {getActorIcon(event.actor.type)}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-sm font-medium">
                          {event.action}
                        </span>
                        <span className="px-2 py-0.5 bg-primary/10 text-primary text-xs rounded">
                          {event.source.app_id}
                        </span>
                      </div>
                      <div className="text-sm text-muted-foreground mt-1">
                        {event.actor.type === 'user'
                          ? event.actor.email || event.actor.user_id
                          : event.actor.type}
                        {event.target && (
                          <span>
                            {' → '}
                            <span className="font-mono">
                              {event.target.type}:{event.target.id}
                            </span>
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {formatDateTime(event.occurred_at)}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </QueryState>
    </div>
  );
}
